import React, { useState } from "react";
import SubPageLayout from "../../components/shared/SubPageLayout";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  Funnel,
  FunnelChart,
} from "recharts";

const KPICard = ({ title, value, trend, change, color }) => (
  <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm transition-all duration-200 hover:shadow-lg dark:shadow-none">
    <div className="flex justify-between items-start">
      <h3 className="text-gray-500 dark:text-gray-500 text-sm font-medium">{title}</h3>
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          trend === "up"
            ? "bg-green-100 text-green-800"
            : "bg-red-100 text-red-800"
        }`}
      >
        {change}
      </span>
    </div>
    <p className="mt-2 text-3xl font-semibold text-gray-900 dark:text-gray-100">{value}</p>
  </div>
);

const MarketingAnalytics = () => {
  const [activeTab, setActiveTab] = useState("sales");
  const [timeRange, setTimeRange] = useState("last30Days");

  const breadcrumbItems = [
    { label: "Marketing & Sales", link: "/marketing-sales" },
    {
      label: "Marketing Analytics",
      link: "/marketing-sales/marketing-analytics",
    },
  ];

  // Mock data for KPIs
  const kpiData = [
    {
      title: "Total Revenue",
      value: "$1.2M",
      trend: "up",
      change: "+15%",
      color: "green",
    },
    {
      title: "Conversion Rate",
      value: "3.8%",
      trend: "up",
      change: "+0.5%",
      color: "green",
    },
    {
      title: "Lead Generation",
      value: "245",
      trend: "down",
      change: "-2%",
      color: "red",
    },
    {
      title: "Client Acquisition",
      value: "32",
      trend: "up",
      change: "+8%",
      color: "green",
    },
  ];

  // Mock data for pipeline stages
  const pipelineData = [
    {
      stage: "Lead Generation",
      value: 120,
      percent: "100%",
      description: "Initial prospects and inquiries",
    },
    {
      stage: "Qualified Leads",
      value: 80,
      percent: "67%",
      description: "Prospects that match ideal client profile",
    },
    {
      stage: "Initial Meeting",
      value: 60,
      percent: "50%",
      description: "First consultation scheduled",
    },
    {
      stage: "Proposal Sent",
      value: 40,
      percent: "33%",
      description: "Investment proposal delivered",
    },
    {
      stage: "Negotiation",
      value: 25,
      percent: "21%",
      description: "Terms discussion phase",
    },
    {
      stage: "Closed Won",
      value: 15,
      percent: "13%",
      description: "Successfully converted to client",
    },
  ];

  // Mock data for campaign performance
  const campaignData = [
    { name: "Email", impressions: 5000, conversions: 250, roi: 320 },
    { name: "Social", impressions: 8000, conversions: 320, roi: 280 },
    { name: "Webinar", impressions: 3000, conversions: 180, roi: 420 },
    { name: "Referral", impressions: 2000, conversions: 160, roi: 580 },
  ];

  // Mock data for engagement metrics
  const engagementData = [
    { month: "Jan", email: 65, social: 45, webinar: 30 },
    { month: "Feb", email: 70, social: 50, webinar: 35 },
    { month: "Mar", email: 75, social: 55, webinar: 40 },
    { month: "Apr", email: 80, social: 60, webinar: 45 },
    { month: "May", email: 85, social: 65, webinar: 50 },
    { month: "Jun", email: 90, social: 70, webinar: 55 },
  ];

  // Mock data for client segmentation
  const segmentationData = [
    { name: "High Net Worth", value: 35 },
    { name: "Mass Affluent", value: 45 },
    { name: "Emerging Wealth", value: 20 },
  ];

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

  const renderSalesAnalytics = () => (
    <div className="space-y-8">
      {/* Pipeline Stages */}
      <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Sales Pipeline
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Visualization of prospect journey from initial contact to client
              conversion
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Total Leads: 120
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Conversion Rate: 13%</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
          <div className="lg:col-span-3">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <FunnelChart>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-surface-default dark:bg-surface-default p-4 shadow-lg dark:shadow-none rounded-lg border">
                            <p className="font-medium text-gray-900 dark:text-gray-100">
                              {data.stage}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {data.description}
                            </p>
                            <div className="mt-2 space-y-1">
                              <p className="text-sm">Count: {data.value}</p>
                              <p className="text-sm">
                                Conversion: {data.percent}
                              </p>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Funnel dataKey="value" data={pipelineData} isAnimationActive>
                    {pipelineData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Funnel>
                </FunnelChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Pipeline Stages
              </h4>
              {pipelineData.map((stage, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center mb-1">
                    <span
                      className="text-sm font-medium text-gray-900 dark:text-gray-100"
                      style={{ color: COLORS[index % COLORS.length] }}
                    >
                      {stage.stage}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {stage.percent}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">{stage.description}</p>
                  <p className="text-sm font-medium mt-1">
                    {stage.value} leads
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Advisor Performance */}
      <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Advisor Performance
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                  Advisor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                  Deals Closed
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                  Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                  Conversion Rate
                </th>
              </tr>
            </thead>
            <tbody className="bg-surface-default dark:bg-surface-default divide-y divide-gray-200">
              {[
                { name: "John Doe", deals: 12, revenue: "$450K", rate: "68%" },
                {
                  name: "Jane Smith",
                  deals: 15,
                  revenue: "$520K",
                  rate: "72%",
                },
                {
                  name: "Mike Johnson",
                  deals: 10,
                  revenue: "$380K",
                  rate: "65%",
                },
              ].map((advisor, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {advisor.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-500">
                    {advisor.deals}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-500">
                    {advisor.revenue}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-500">
                    {advisor.rate}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderMarketingAnalytics = () => (
    <div className="space-y-8">
      {/* Campaign Performance */}
      <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Campaign Performance
        </h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={campaignData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="impressions" fill="#0088FE" />
              <Bar dataKey="conversions" fill="#00C49F" />
              <Bar dataKey="roi" fill="#FFBB28" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Engagement Metrics */}
      <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Engagement Metrics
        </h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={engagementData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area
                type="monotone"
                dataKey="email"
                stackId="1"
                stroke="#0088FE"
                fill="#0088FE"
              />
              <Area
                type="monotone"
                dataKey="social"
                stackId="1"
                stroke="#00C49F"
                fill="#00C49F"
              />
              <Area
                type="monotone"
                dataKey="webinar"
                stackId="1"
                stroke="#FFBB28"
                fill="#FFBB28"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Client Segmentation */}
      <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Client Segmentation
        </h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={segmentationData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {segmentationData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );

  const renderKPIDashboard = () => (
    <div className="space-y-8">
      {/* KPI Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiData.map((kpi, index) => (
          <KPICard key={index} {...kpi} />
        ))}
      </div>

      {/* Custom Dashboard */}
      <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Custom Dashboard
          </h3>
          <button className="px-4 py-2 bg-[#202EC6] text-white rounded-lg hover:bg-opacity-90">
            Add Widget
          </button>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div className="border rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              Revenue Trend
            </h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={engagementData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="email" stroke="#0088FE" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="border rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              Lead Sources
            </h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={segmentationData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {segmentationData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <SubPageLayout
      title="Marketing Analytics"
      subtitle="Comprehensive insights into Sales and Marketing performance"
      breadcrumbItems={breadcrumbItems}
    >
      <div className="p-6 space-y-6">
        {/* Introduction Banner */}
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Welcome to Marketing Analytics
          </h2>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Track your marketing performance, analyze sales data, and make
            data-driven decisions with our comprehensive analytics suite.
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-500">
            Real-time insights for data-driven decisions
          </p>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex gap-4 items-center">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="border-gray-300 rounded-lg text-gray-900 dark:text-gray-100"
            >
              <option value="last7Days">Last 7 Days</option>
              <option value="last30Days">Last 30 Days</option>
              <option value="last90Days">Last 90 Days</option>
              <option value="custom">Custom Range</option>
            </select>
            <button className="px-4 py-2 bg-[#202EC6] text-white rounded-lg hover:bg-opacity-90">
              Download Report
            </button>
          </div>
          <div className="flex rounded-lg border border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setActiveTab("sales")}
              className={`px-4 py-2 text-sm font-medium rounded-l-lg ${
                activeTab === "sales"
                  ? "bg-[#202EC6] text-white"
                  : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
              }`}
            >
              Sales Analytics
            </button>
            <button
              onClick={() => setActiveTab("marketing")}
              className={`px-4 py-2 text-sm font-medium border-x ${
                activeTab === "marketing"
                  ? "bg-[#202EC6] text-white"
                  : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
              }`}
            >
              Marketing Analytics
            </button>
            <button
              onClick={() => setActiveTab("kpi")}
              className={`px-4 py-2 text-sm font-medium rounded-r-lg ${
                activeTab === "kpi"
                  ? "bg-[#202EC6] text-white"
                  : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
              }`}
            >
              KPI Dashboard
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === "sales" && renderSalesAnalytics()}
        {activeTab === "marketing" && renderMarketingAnalytics()}
        {activeTab === "kpi" && renderKPIDashboard()}
      </div>
    </SubPageLayout>
  );
};

export default MarketingAnalytics;
